import React, { useState, useEffect } from "react";
import { Button, Form, Table } from "react-bootstrap";
import { Autocomplete, Box, TextField } from "@mui/material";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { MecordColors } from "Constants/Color";
import {
  autocompleteDropdownStyles,
  getDisabledInputStyles,
} from "Constants/Style";
import AddButton from "Common/Components/Buttons/AddButton";
import { fetchModelByDropDown } from "slices/Masters/model/thunk";
import { fetchProductsDropDownByModel } from "slices/Masters/product/thunk";
import NoSelectedProducts from "assets/images/Order/NoSelectedProducts.png";

interface EnquiryProductFormProps {
  onAdd: (values: any) => void;
  items: any[];
  setItems: React.Dispatch<React.SetStateAction<any[]>>;
  onAddNewProduct?: () => void;
}

type ProductFormValues = {
  model: string;
  model_name: string;
  productName: string;
  quantity: string;
  unit_price: string;
  category: string;
  category_name: string;
  sub_category: string;
  sub_category_name: string;
  make: string;
  make_name: string;
  unit: string;
  unit_name: string;
  item: string;
  item_name: string;
  sku: string;
  product_sku: string;
  hsn: string;
  hsn_code: string;
  gst_percent: number;
  gst_percentage: number;
  product_type: any[];
  [key: string]: any;
};

const commonInputStyle = {
  fontFamily: "Poppins, sans-serif",
  fontSize: "13px",
};

const EnquiryProductForm: React.FC<EnquiryProductFormProps> = ({
  onAdd,
  items,
  setItems,
  onAddNewProduct,
}) => {
  const dispatch = useDispatch<any>();

  const initialFormState: ProductFormValues = {
    model: "",
    model_name: "",
    productName: "",
    quantity: "",
    unit_price: "",
    category: "",
    category_name: "",
    sub_category: "",
    sub_category_name: "",
    make: "",
    make_name: "",
    unit: "",
    unit_name: "",
    item: "",
    item_name: "",
    sku: "",
    product_sku: "",
    hsn: "",
    hsn_code: "",
    gst_percent: 0,
    gst_percentage: 0,
    product_type: [],
  };

  const [formData, setFormData] = useState<ProductFormValues>(initialFormState);
  const [modelOptions, setModelOptions] = useState<any[]>([]);
  const [productOptions, setProductOptions] = useState<any[]>([]);
  const [selectedModel, setSelectedModel] = useState<any>(null);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [editableRowId, setEditableRowId] = useState<string | number | null>(
    null
  );
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const [loadingProducts, setLoadingProducts] = useState(false)

  // Validation Schema
  const validationSchema = Yup.object().shape({
    model: Yup.string().required("Model is required"),
    productName: Yup.string().required("Product is required"),
    quantity: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value
      )
      .required("Quantity is required")
      .positive("Quantity must be positive")
      .typeError("Quantity must be a number"),
    unit_price: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value
      )
      .min(0, "Unit Price cannot be negative")
      .typeError("Unit Price must be a number")
      .notRequired(),
  });

  // Fetch Models on Mount
  useEffect(() => {
    dispatch(fetchModelByDropDown())
      .unwrap()
      .then((res: any) => {
        setModelOptions(res?.data || []);
      })
      .catch((err: any) => {
        console.error("Error fetching models:", err);
        toast.error("Failed to fetch models");
      });
  }, [dispatch]);

  const handleModelSelect = (_: any, selectedModel: any | null) => {
    setSelectedModel(selectedModel);

    if (!selectedModel) {
      handleFormReset();
      return;
    }

    const model_id = selectedModel.model_id;

    setFormData((prev) => ({
      ...prev,
      model: model_id,
      model_name: selectedModel.model_name,
      productName: "",
      unit_price: "",
      quantity: "",
      item: "",
      item_name: "",
    }));

    setSelectedProduct(null);
    setProductOptions([]);

    setLoadingProducts(true)
    dispatch(fetchProductsDropDownByModel(model_id))
      .unwrap()
      .then((payload: any) => {
        const products = Array.isArray(payload.data) ? payload.data : [];
        setProductOptions(products);
      })
      .catch(() => toast.error("Error fetching products for selected model"))
      .finally(() => setLoadingProducts(false))
  };

  const handleProductSelect = (_: any, selectedProduct: any) => {
    setSelectedProduct(selectedProduct);

    if (selectedProduct) {
      setFormData((prev) => ({
        ...prev,
        item:
          selectedProduct.id ||
          selectedProduct.product_id ||
          selectedProduct.item_id ||
          "",
        productName:
          selectedProduct.product_name || selectedProduct.item_name || "",
        item_name:
          selectedProduct.product_name || selectedProduct.item_name || "",
        unit_price:
          selectedProduct.unit_price?.toString() ||
          selectedProduct.selling_price?.toString() ||
          "",
        category:
          selectedProduct.category?.category_id ||
          selectedProduct.category?.category ||
          "",
        category_name: selectedProduct.category?.category_name || "",
        sub_category:
          selectedProduct.sub_category?.sub_category_id ||
          selectedProduct.sub_category?.sub_category ||
          "",
        sub_category_name:
          selectedProduct.sub_category?.sub_category_name || "",
        make: selectedProduct.make?.make_id || "",
        make_name: selectedProduct.make?.make_name || "",
        model: selectedProduct.model?.model_id || prev.model,
        model_name: selectedProduct.model?.model_name || prev.model_name,
        hsn: selectedProduct.hsn_id || selectedProduct.hsn?.hsn_code_id || "",
        hsn_code: selectedProduct.hsn?.hsn_code || "",
        unit: selectedProduct.unit?.unit_id || selectedProduct.unit?.unit || "",
        unit_name: selectedProduct.unit?.unit_name || "",
        sku:
          selectedProduct.sku ||
          selectedProduct.item_sku ||
          selectedProduct.product_sku ||
          "",
        product_sku:
          selectedProduct.product_sku ||
          selectedProduct.sku ||
          selectedProduct.item_sku ||
          "",
        gst_percent:
          selectedProduct.gst_percent ||
          selectedProduct.hsn?.gst?.gst_percentage ||
          0,
        gst_percentage:
          selectedProduct.hsn?.gst?.gst_percentage ||
          selectedProduct.gst_percent ||
          0,
        product_type: selectedProduct.product_types || [],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        item: "",
        productName: "",
        item_name: "",
        unit_price: "",
        category: "",
        category_name: "",
        sub_category: "",
        sub_category_name: "",
        make: "",
        make_name: "",
        hsn: "",
        hsn_code: "",
        unit: "",
        unit_name: "",
        sku: "",
        product_sku: "",
        gst_percent: 0,
        gst_percentage: 0,
        product_type: [],
      }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFormReset = () => {
    setFormData(initialFormState);
    setProductOptions([]);
    setSelectedModel(null);
    setSelectedProduct(null);
    setErrors({});
  };

  const handleSubmit = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setErrors({});

      const payload: any = {
        ...formData,
        id: Date.now(),
        type: "product",
      };

      if (!payload.item_name) {
        payload.item_name = formData.productName || formData.item_name || "";
      }

      const qty = parseFloat(payload.quantity) || 0;
      const price = parseFloat(payload.unit_price) || 0;
      payload.total = (qty * price).toFixed(2);
      payload.total_price = payload.total;

      onAdd(payload);
      toast.success("Product added successfully!");
      handleFormReset();
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path] = error.message;
          }
        });
        setErrors(newErrors);
        toast.error("Please fill all required fields correctly.");
      }
    }
  };

  const handleDelete = (index: number) => {
    const updated = [...items];
    updated.splice(index, 1);
    setItems(updated);
    toast.success("Product deleted successfully!");
  };

  const handleItemUpdate = (index: number, field: string, value: any) => {
    const updatedItems = [...items];
    const updatedItem = {
      ...updatedItems[index],
      [field]: value,
    };

    if (field === "quantity" || field === "unit_price") {
      const qty =
        parseFloat(field === "quantity" ? value : updatedItem.quantity) || 0;
      const price =
        parseFloat(field === "unit_price" ? value : updatedItem.unit_price) ||
        0;
      updatedItem.total = (qty * price).toFixed(2);
      updatedItem.total_price = updatedItem.total;
    }

    updatedItems[index] = updatedItem;
    setItems(updatedItems);
  };

  return (
    <div
      className="p-2 rounded bg-opacity-10 "
      style={{
        background: MecordColors.ActiveBackground,
      }}
    >
      <Form onSubmit={(e) => e.preventDefault()}>
        <div className="row g-3 mb-3">
          {/* Model */}
          <div className="col-12 col-md-6 col-lg-2">
            <Form.Group>
              <Form.Label>
                Model <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                options={modelOptions}
                getOptionLabel={(option: any) => option?.model_name || ""}
                value={selectedModel}
                onChange={handleModelSelect}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt?.model_name || ""
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Model"
                    variant="outlined"
                    size="small"
                    error={!!errors.model}
                    helperText={errors.model}
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      ...getDisabledInputStyles(false),
                    }}
                  />
                )}
              />
            </Form.Group>
          </div>

          {/* Product Dropdown */}
          <div className="col-12 col-md-6 col-lg-2">
            <Form.Group>
              <Form.Label>
                Product <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                options={productOptions}
                loading={loadingProducts}
                getOptionLabel={(option: any) => option?.product_name || ""}
                value={selectedProduct}
                onChange={handleProductSelect}
                disabled={!selectedModel}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.product_id} style={{ padding: 0 }}>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.8,
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: MecordColors.Hover,
                          color: MecordColors.ActiveText,
                        },
                        ...(selected && {
                          backgroundColor: MecordColors.ActiveBackground,
                          color: MecordColors.ActiveText,
                        }),
                      }}
                    >
                      <Box sx={{ width: "100%" }}>
                        {/* Product Name */}
                        <Box sx={{ fontWeight: "bold", mb: 0.5 }}>
                          {option.product_name}
                        </Box>

                        {/* Make & Model */}
                        <Box
                          sx={{
                            fontSize: "0.75rem",
                            color: "#666",
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <span>Make: {option.make?.make_name || "N/A"}</span>
                          <span>
                            Model: {option.model?.model_name || "N/A"}
                          </span>
                        </Box>

                        {/* SKU, HSN, Price */}
                        <Box
                          sx={{
                            fontSize: "0.75rem",
                            color: "#666",
                            display: "flex",
                            justifyContent: "space-between",
                            mt: 0.3,
                          }}
                        >
                          <span>SKU: {option.product_sku || "N/A"}</span>
                          <span>HSN: {option.hsn?.hsn_code || "N/A"}</span>
                          <span
                            style={{ fontWeight: "bold", color: "#007bff" }}
                          >
                            ₹{option.unit_price || "N/A"}
                          </span>
                        </Box>
                      </Box>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Product"
                    variant="outlined"
                    size="small"
                    error={!!errors.productName}
                    helperText={errors.productName}
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      ...getDisabledInputStyles(!selectedModel),
                    }}
                  />
                )}
              />
            </Form.Group>
          </div>

          {/* Product Name Readonly */}
          {/* <div className="col-12 col-md-6 col-lg-2">
            <Form.Group>
              <Form.Label>Product Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.productName}
                readOnly
                placeholder="Auto-filled"
                style={{ ...commonInputStyle, backgroundColor: "#f8f9fa" }}
                className="form-control-placeholder"
              />
            </Form.Group>
          </div> */}

          {/* Quantity */}
          <div className="col-12 col-md-6 col-lg-2">
            <Form.Group>
              <Form.Label>
                Quantity <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleInputChange}
                placeholder="Enter Quantity"
                style={commonInputStyle}
                className="form-control-placeholder"
                isInvalid={!!errors.quantity}
              />
              {errors.quantity && (
                <small className="text-danger">{errors.quantity}</small>
              )}
            </Form.Group>
          </div>

          {/* Unit Price */}
          <div className="col-12 col-md-6 col-lg-2">
            <Form.Group>
              <Form.Label>
                Unit Price
              </Form.Label>
              <Form.Control
                type="number"
                name="unit_price"
                value={formData.unit_price}
                onChange={handleInputChange}
                placeholder="Enter Price"
                style={commonInputStyle}
                className="form-control-placeholder"
                isInvalid={!!errors.unit_price}
              />
              {errors.unit_price && (
                <small className="text-danger">{errors.unit_price}</small>
              )}
            </Form.Group>
          </div>

          {/* Add Button */}
          <div className="col-12 col-md-6 col-lg-2 d-flex align-items-end">
            <AddButton
              onClick={handleSubmit}
              label="Add"
              fullWidth={true}
            />
          </div>
        </div>
      </Form>

      {/* Table */}
      {items.length > 0 ? (
        <div
          className="table-responsive"
          style={{
            background: "#FFFFFF",
            border: "1px solid #DDE1EF",
            borderRadius: "5px",
          }}
        >
          <Table className="table align-middle mb-0">
            <thead
              style={{
                backgroundColor: MecordColors?.LightGrayColor,
                color: "#fff",
              }}
            >
              <tr>
                <th style={{ minWidth: "80px" }}>Sr No.</th>
                <th style={{ minWidth: "200px" }}>Product</th>
                <th style={{ minWidth: "150px" }}>Make</th>
                <th style={{ minWidth: "150px" }}>Model</th>
                <th style={{ minWidth: "120px" }}>Quantity</th>
                <th style={{ minWidth: "100px" }}>Unit</th>
                <th style={{ minWidth: "150px" }}>Unit Price</th>
                <th style={{ minWidth: "150px" }}>Total</th>
                <th
                  style={{
                    minWidth: "100px",
                    position: "sticky",
                    right: 0,
                    zIndex: 5,
                    backgroundColor: MecordColors?.LightGrayColor,
                  }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item: any, index: number) => {
                const rowId = item.id;
                // const isEditable = editableRowId === rowId; // Removed

                return (
                  <tr key={item.product_id}>
                    <td>{index + 1}</td>
                    <td>{item?.item_name || item?.productName}</td>
                    <td>{item?.make_name || "-"}</td>
                    <td>{item?.model_name || "-"}</td>
                    <td>{item?.productQuantity || item?.quantity}</td>

                    {/* Quantity Editable */}
                    {/* <td>
                                            <Form.Control
                                                type="number"
                                                min="1"
                                                value={item.quantity}
                                                // readOnly={!isEditable} // Always editable
                                                onChange={(e) => handleItemUpdate(index, "quantity", e.target.value)}
                                                size="sm"
                                                style={commonInputStyle}
                                                className="form-control-placeholder"
                                            />
                                        </td> */}

                    <td>{item?.unit_name || item?.unit || "-"}</td>

                    {/* Unit Price Editable */}
                    <td>
                      <div className="d-flex align-items-center">
                        <span className="me-1">₹</span>
                        <Form.Control
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={item.unit_price}
                          // readOnly={!isEditable} // Always editable
                          onChange={(e) =>
                            handleItemUpdate(
                              index,
                              "unit_price",
                              e.target.value
                            )
                          }
                          size="sm"
                          style={{ ...commonInputStyle, paddingLeft: "5px" }}
                          className="form-control-placeholder"
                        />
                      </div>
                    </td>

                    {/* Total */}
                    <td>
                      ₹{" "}
                      {item.total ||
                        (
                          Number(item.quantity) * Number(item.unit_price)
                        ).toFixed(2)}
                    </td>

                    {/* Actions */}
                    <td
                      className="text-center"
                      style={{
                        position: "sticky",
                        right: 0,
                        background: "white",
                        zIndex: 5,
                      }}
                    >
                      <i
                        className="bi bi-trash text-danger"
                        style={{ cursor: "pointer" }}
                        title="Delete Product"
                        onClick={() => handleDelete(index)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      ) : (
        <div className="text-center text-muted py-4 border rounded p-3 bg-white">
          <img
            src={NoSelectedProducts}
            alt="No products selected"
            className="mb-3"
            style={{ maxWidth: "180px", height: "auto" }}
          />
          <div>Search existing products to add to this list or add</div>
          <div>new products to get started</div>
          {onAddNewProduct && (
            <Button
              variant="light"
              className="mt-2"
              style={{
                backgroundColor: MecordColors.LightThemeBGColor,
                borderColor: MecordColors.LightThemeBGColor,
                minWidth: "80px",
                color: MecordColors.ThemeBGColor,
              }}
              onClick={onAddNewProduct}
            >
              {" "}
              + Add New Product
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default EnquiryProductForm;
